import React, { useEffect, useState } from 'react';
import { connectAuthPost } from './connectApi';
import ProfileLink from './ProfileLink';
import './styles/FollowsList.css';

/**
 * Who follows a member, or who they follow, in a modal with a tab for each.
 * The server decides whether the list is visible; this only renders it.
 */
export default function FollowsList({ userId, initialTab = 'followers', onClose, onCountsChange }) {
  const [tab, setTab] = useState(initialTab);
  const [people, setPeople] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setNotice('');
      try {
        const res = await connectAuthPost('connect/users/follows', { user_id: userId, type: tab });
        if (cancelled) return;
        if (res.success) setPeople(res.data.people || []);
        else { setPeople([]); setNotice(res.response || 'This list is not visible.'); }
      } catch (err) {
        if (!cancelled) setNotice('Could not reach the server.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [userId, tab]);

  const toggleFollow = async (personId) => {
    try {
      const res = await connectAuthPost('connect/users/follow', { user_id: personId });
      if (res.success) {
        setPeople((prev) => prev.map((p) =>
          p.id === personId ? { ...p, is_following: res.data.is_following } : p));
        if (onCountsChange) onCountsChange();
      }
    } catch (err) { /* leave the row as it was */ }
  };

  const empty = tab === 'followers' ? 'No followers yet.' : 'Not following anyone yet.';

  return (
    <div className="follows-overlay" onClick={onClose}>
      <div className="follows-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="follows-head">
          <div className="follows-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'followers'}
                    className={tab === 'followers' ? 'active' : ''} onClick={() => setTab('followers')}>
              Followers
            </button>
            <button type="button" role="tab" aria-selected={tab === 'following'}
                    className={tab === 'following' ? 'active' : ''} onClick={() => setTab('following')}>
              Following
            </button>
          </div>
          <button type="button" className="follows-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="follows-body">
          {isLoading ? (
            <p className="follows-empty">Loading…</p>
          ) : notice ? (
            <p className="follows-empty">{notice}</p>
          ) : people.length === 0 ? (
            <p className="follows-empty">{empty}</p>
          ) : (
            <ul className="follows-list">
              {people.map((p) => (
                <li key={p.id} className="follows-row">
                  <ProfileLink userId={p.id} avatar onClick={onClose}>
                    {p.profile_picture
                      ? <img className="follows-avatar" src={p.profile_picture} alt="" />
                      : <span className="follows-avatar placeholder">{(p.full_name || '?').charAt(0).toUpperCase()}</span>}
                  </ProfileLink>
                  <div className="follows-name">
                    <ProfileLink userId={p.id} onClick={onClose}>{p.full_name}</ProfileLink>
                    {p.pronouns && <span className="follows-pronouns">{p.pronouns}</span>}
                  </div>
                  {!p.is_me && (
                    <button type="button"
                            className={`follows-btn ${p.is_following ? 'following' : ''}`}
                            onClick={() => toggleFollow(p.id)}>
                      {p.is_following ? 'Following' : 'Follow'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
